'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { api } from '@/lib/api';

interface Location {
  id: string;
  name: string;
  type: 'state' | 'district' | 'town';
  full_name: string;
  district?: string;
  state?: string;
}

interface HeroSectionProps {
  areas: { id: string; name: string; agentCount: number }[];
  onAreaSelect: (areaId: string, locationType?: string) => void;
}

const HeroSection = ({ areas: _areas, onAreaSelect }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Location[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get<{ locations: Location[] }>(
        `/locations/search?q=${encodeURIComponent(query)}`
      );
      setSuggestions(response.locations);
      setIsOpen(response.locations.length > 0);
      setHighlightedIndex(-1);
    } catch {
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (!selectedLocation) {
        fetchSuggestions(locationQuery);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [locationQuery, fetchSuggestions, selectedLocation]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocationQuery(value);
    setSelectedLocation(null);
  };

  const handleSelect = (location: Location) => {
    setLocationQuery(location.name);
    setSelectedLocation(location);
    setIsOpen(false);
    setSuggestions([]);
  };

  const handleSearchClick = () => {
    if (selectedLocation) {
      // Pass the location name, not the ID, for better filtering
      onAreaSelect(selectedLocation.name, selectedLocation.type);
    } else if (locationQuery.trim()) {
      onAreaSelect(locationQuery.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearchClick();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        } else {
          handleSearchClick();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'state':
        return 'Negeri';
      case 'district':
        return 'Daerah';
      case 'town':
        return 'Bandar';
      default:
        return '';
    }
  };

  if (!isHydrated) {
    return (
      <section className="hero-gradient relative min-h-[750px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-[150px]"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary rounded-full blur-[150px]"></div>
        </div>
        <div className="max-w-[1280px] mx-auto px-6 relative z-10 text-center py-20">
          <h2 className="text-5xl md:text-7xl font-heading font-black leading-tight tracking-tight mb-6">
            <span className="text-white">Kekemasan Bertaraf </span>
            <span className="text-accent">Hotel, Terus Ke Rumah Anda</span>
          </h2>
          <p className="text-purple-100/80 text-lg mb-12 max-w-3xl mx-auto font-body">
            Servis lipat & gosok baju dari rumah oleh ejen komuniti setempat. Tanpa perlu ke dobi.
            Tanpa timbang. Tempah terus dengan ejen berdekatan anda.
          </p>
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-brandDark/80 backdrop-blur-md p-3 rounded-full shadow-2xl flex flex-col md:flex-row items-center gap-3 ring-1 ring-accent/30">
              <div className="flex-1 flex items-center px-6 w-full">
                <Icon name="MapPinIcon" size={20} variant="solid" className="text-accent mr-3" />
                <div className="h-12 bg-white/20 rounded-lg animate-pulse flex-1"></div>
              </div>
              <button className="w-full md:w-auto bg-accent text-secondary px-12 py-4 rounded-full font-cta font-black text-sm flex items-center justify-center gap-2 hover:bg-white transition-all shadow-xl">
                MULA SEKARANG
                <Icon name="ArrowRightIcon" size={16} variant="outline" />
              </button>
            </div>
          </div>
          <div className="pt-4" />
        </div>
      </section>
    );
  }

  return (
    <section className="hero-gradient relative min-h-[750px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-[150px]"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary rounded-full blur-[150px]"></div>
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10 text-center py-20">
        <h2 className="text-5xl md:text-7xl font-heading font-black leading-tight tracking-tight mb-6">
          <span className="text-white">Kekemasan Bertaraf </span>
          <span className="text-accent">Hotel, Terus Ke Rumah Anda</span>
        </h2>
        <p className="text-purple-100/80 text-lg mb-12 max-w-3xl mx-auto font-body">
          Servis lipat & gosok baju dari rumah oleh ejen komuniti setempat. Tanpa perlu ke dobi.
          Tanpa timbang. Tempah terus dengan ejen berdekatan anda.
        </p>

        <div className="max-w-4xl mx-auto mb-16">
          <div
            ref={wrapperRef}
            className="bg-brandDark/80 backdrop-blur-md p-3 rounded-3xl md:rounded-full shadow-2xl flex flex-col md:flex-row items-center gap-3 ring-1 ring-accent/30 relative"
          >
            <div className="flex-1 flex items-center px-6 w-full relative">
              <Icon name="MapPinIcon" size={20} variant="solid" className="text-accent mr-3 shrink-0" />
              <input
                value={locationQuery}
                onChange={handleLocationChange}
                onKeyDown={handleKeyDown}
                onFocus={() => locationQuery.length >= 2 && suggestions.length > 0 && setIsOpen(true)}
                className="w-full bg-transparent border-none focus:ring-0 focus:outline-none focus-visible:outline-none text-lg font-body font-medium text-white placeholder:text-white/60"
                placeholder="Di manakah lokasi anda hari ini?"
                type="text"
              />
              {isLoading && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent border-t-transparent" />
                </div>
              )}
            </div>
            <button
              onClick={handleSearchClick}
              disabled={!locationQuery.trim()}
              className="w-full md:w-auto bg-accent text-secondary px-12 py-4 rounded-full font-cta font-black text-sm flex items-center justify-center gap-2 hover:bg-white transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Mula Sekarang"
            >
              MULA SEKARANG
              <Icon name="ArrowRightIcon" size={16} variant="outline" />
            </button>

            {/* Suggestions Dropdown */}
            {isOpen && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-2xl shadow-2xl max-h-80 overflow-y-auto z-50">
                {suggestions.map((location, index) => (
                  <button
                    key={location.id}
                    type="button"
                    onClick={() => handleSelect(location)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full px-5 py-3.5 text-left flex items-center gap-3 transition-colors ${
                      index === highlightedIndex ? 'bg-muted' : 'hover:bg-muted/50'
                    } ${index !== suggestions.length - 1 ? 'border-b border-border/50' : ''}`}
                  >
                    <Icon
                      name="MapPinIcon"
                      size={18}
                      variant="solid"
                      className="text-accent shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-textPrimary truncate">
                          {location.name}
                        </span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                            location.type === 'state'
                              ? 'bg-blue-100 text-blue-700'
                              : location.type === 'district'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {getTypeLabel(location.type)}
                        </span>
                      </div>
                      {location.type !== 'state' && (
                        <p className="text-xs text-textSecondary truncate">
                          {location.type === 'town'
                            ? `${location.district}, ${location.state}`
                            : location.state}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pt-4" />
        <div className="max-w-4xl mx-auto text-left mt-8 px-6">
          <h4 className="text-2xl md:text-3xl font-heading font-black text-accent mb-4">
            APA ITU SURILIPAT?
          </h4>
          <p className="text-purple-100/80 text-base md:text-lg font-body mb-4">
            Surilipat ialah platform yang menghubungkan pelanggan dengan ejen lipat dan gosok baju
            dari rumah. Semua ejen adalah individu dari komuniti setempat yang menawarkan servis
            kemasan pakaian secara fleksibel, telus dan berpatutan.
          </p>
          <div>
            <p className="text-purple-100/80 text-base md:text-lg font-body mb-2">Anda boleh:</p>
            <ul className="list-disc list-inside text-purple-100/80 text-base md:text-lg font-body space-y-1">
              <li>Cari ejen berdekatan berdasarkan lokasi</li>
              <li>Berbincang terus melalui WhatsApp</li>
              <li>Menikmati servis kemasan tanpa perlu keluar rumah</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
